import { useRef } from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/lib/cn'

export type SegmentedControlSize = 'sm' | 'md'
export type SegmentedControlVariant = 'bar' | 'cards'

export interface SegmentedControlOption<T extends string = string> {
  value: T
  label: string
  icon?: React.ComponentType<{ className?: string }>
  /** Secondary line. Shown in the `cards` variant only. */
  description?: string
  disabled?: boolean
}

export interface SegmentedControlProps<T extends string = string> {
  options: SegmentedControlOption<T>[]
  value: T
  onChange: (value: T) => void
  /** Required: the control has no visible label of its own. */
  ariaLabel: string
  /** `bar` = pill track with a sliding thumb. `cards` = stacked option cards. */
  variant?: SegmentedControlVariant
  /** Bar variant only. */
  size?: SegmentedControlSize
  disabled?: boolean
  className?: string
}

/**
 * `track` padding and `thumbInset` must agree: the thumb's width is the track's
 * inner width divided by the option count, so the padding is subtracted twice.
 */
const barSizeClasses: Record<
  SegmentedControlSize,
  { track: string; thumbInset: string; padding: string; option: string; icon: string }
> = {
  sm: {
    track: 'h-8 p-0.5 text-xs',
    thumbInset: 'inset-y-0.5 left-0.5',
    padding: '0.25rem',
    option: 'gap-1.5 px-2.5',
    icon: 'h-3.5 w-3.5',
  },
  md: {
    track: 'h-10 p-1 text-sm',
    thumbInset: 'inset-y-1 left-1',
    padding: '0.5rem',
    option: 'gap-2 px-3',
    icon: 'h-4 w-4',
  },
}

/**
 * Radio group rendered as a segmented control — every choice stays on screen, so
 * changing one costs a single click instead of open-then-pick.
 *
 * Use it instead of a `<Select>` whenever there are two or three mutually
 * exclusive options with short labels (role, status, on/off filters). Beyond
 * three, or with long labels, a select still wins.
 *
 * Keyboard follows the ARIA radiogroup pattern: one tab stop, arrow keys move
 * between options and select as they go, Home/End jump to the ends.
 *
 * @example
 * <SegmentedControl
 *   ariaLabel="Member role"
 *   options={[{ value: 'member', label: 'Member' }, { value: 'manager', label: 'Manager' }]}
 *   value={role}
 *   onChange={setRole}
 * />
 */
export function SegmentedControl<T extends string = string>({
  options,
  value,
  onChange,
  ariaLabel,
  variant = 'bar',
  size = 'md',
  disabled = false,
  className,
}: SegmentedControlProps<T>): React.ReactElement {
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([])
  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => option.value === value)
  )

  /** Arrow keys skip disabled options and wrap at both ends. */
  const moveFocus = (fromIndex: number, step: number): void => {
    const count = options.length
    for (let hop = 1; hop <= count; hop++) {
      const nextIndex = (fromIndex + step * hop + count * count) % count
      const next = options[nextIndex]
      if (next.disabled) continue
      onChange(next.value)
      optionRefs.current[nextIndex]?.focus()
      return
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent, index: number): void => {
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault()
        moveFocus(index, 1)
        break
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault()
        moveFocus(index, -1)
        break
      case 'Home':
        event.preventDefault()
        moveFocus(-1, 1)
        break
      case 'End':
        event.preventDefault()
        moveFocus(0, -1)
        break
      default:
        break
    }
  }

  if (variant === 'cards') {
    return (
      <div
        role="radiogroup"
        aria-label={ariaLabel}
        className={cn('grid gap-2', className)}
      >
        {options.map((option, index) => {
          const selected = option.value === value
          const Icon = option.icon
          const isDisabled = disabled || option.disabled
          return (
            <button
              key={option.value}
              ref={(node) => {
                optionRefs.current[index] = node
              }}
              type="button"
              role="radio"
              aria-checked={selected}
              tabIndex={index === selectedIndex ? 0 : -1}
              disabled={isDisabled}
              onClick={() => onChange(option.value)}
              onKeyDown={(event) => handleKeyDown(event, index)}
              className={cn(
                'group/segment flex w-full cursor-pointer items-start gap-3 rounded-xl border px-3.5 py-3 text-left',
                'transition-[background-color,border-color,box-shadow] duration-150 motion-reduce:transition-none',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2',
                'disabled:cursor-not-allowed disabled:opacity-50',
                selected
                  ? 'border-primary/40 bg-primary/5 shadow-[0_1px_2px_rgba(2,6,23,0.04),0_4px_12px_rgba(0,74,107,0.07)] dark:border-primary/50 dark:bg-primary/10'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600 dark:hover:bg-gray-700/50'
              )}
            >
              {Icon ? (
                <Icon
                  className={cn(
                    'mt-0.5 h-4 w-4 shrink-0',
                    selected
                      ? 'text-primary dark:text-sky-300'
                      : 'text-gray-400 dark:text-gray-500'
                  )}
                />
              ) : null}
              <span className="min-w-0 flex-1">
                <span
                  className={cn(
                    'block text-sm font-medium',
                    selected
                      ? 'text-primary dark:text-sky-200'
                      : 'text-gray-900 dark:text-gray-100'
                  )}
                >
                  {option.label}
                </span>
                {option.description ? (
                  <span
                    className={cn(
                      'mt-1 block text-xs leading-5',
                      selected
                        ? 'text-primary/80 dark:text-sky-200/80'
                        : 'text-gray-500 dark:text-gray-400'
                    )}
                  >
                    {option.description}
                  </span>
                ) : null}
              </span>
              <span
                aria-hidden
                className={cn(
                  'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors duration-150 motion-reduce:transition-none',
                  selected
                    ? 'border-primary bg-primary text-white dark:border-sky-400 dark:bg-sky-400 dark:text-gray-900'
                    : 'border-gray-300 bg-white text-transparent dark:border-gray-600 dark:bg-gray-800'
                )}
              >
                <Check className="h-2.5 w-2.5" strokeWidth={3} />
              </span>
            </button>
          )
        })}
      </div>
    )
  }

  const bar = barSizeClasses[size]

  return (
    // inline-grid with auto-cols-fr, not flex: equal slot widths are what let a
    // single thumb translate by exactly 100% of itself per step.
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        'relative inline-grid w-fit max-w-full grid-flow-col auto-cols-fr items-stretch rounded-lg border border-gray-200 bg-gray-100/80 dark:border-gray-700 dark:bg-gray-800',
        disabled && 'opacity-50',
        bar.track,
        className
      )}
    >
      {/* Sliding thumb. One element travelling between slots reads as a single
          state moving, where per-option background fades read as two blinks. */}
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute rounded-[7px] border border-gray-200/80 bg-white shadow-[0_1px_2px_rgba(2,6,23,0.06),0_1px_3px_rgba(2,6,23,0.04)] transition-transform duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none dark:border-gray-600 dark:bg-gray-700',
          bar.thumbInset
        )}
        style={{
          width: `calc((100% - ${bar.padding}) / ${options.length})`,
          transform: `translateX(calc(${selectedIndex} * 100%))`,
        }}
      />

      {options.map((option, index) => {
        const selected = option.value === value
        const Icon = option.icon
        const isDisabled = disabled || option.disabled
        return (
          <button
            key={option.value}
            ref={(node) => {
              optionRefs.current[index] = node
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={index === selectedIndex ? 0 : -1}
            disabled={isDisabled}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={cn(
              'relative z-10 flex cursor-pointer items-center justify-center whitespace-nowrap rounded-[7px] font-medium',
              'transition-colors duration-150 motion-reduce:transition-none',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
              'disabled:cursor-not-allowed disabled:opacity-50',
              bar.option,
              selected
                ? 'text-gray-900 dark:text-gray-100'
                : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
            )}
          >
            {Icon ? (
              <Icon
                className={cn(
                  'shrink-0',
                  bar.icon,
                  selected
                    ? 'text-primary dark:text-sky-300'
                    : 'text-gray-400 dark:text-gray-500'
                )}
                aria-hidden
              />
            ) : null}
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

SegmentedControl.displayName = 'SegmentedControl'
